import React from 'react';
import { Box, Container, Typography, Divider } from '@mui/material';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

const UPDATED_AT = 'November 9, 2025';

const PrivacyPolicy: React.FC = () => {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TopNavBar title="Privacy Policy" canGoBack />

      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          py: 4,
          color: '#1C1B1F',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            ifinds Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: '#616161' }}>
            Last updated: {UPDATED_AT}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          Welcome to ifinds. This Privacy Policy explains how Bilvacrop Technologies Private
          Limited (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) collects, uses, discloses, and protects your information
          when you use the ifinds mobile application, website, and related services (collectively, the
          &quot;Services&quot;). By accessing or using our Services, you agree to the collection and use of
          information in accordance with this Privacy Policy.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          1. Information We Collect
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We collect information that you provide to us directly, such as when you create an account,
          update your profile, post content, make bookings, or contact support. This can include your
          name, username, email address, phone number, date of birth, payment details, profile photo,
          travel preferences, and any other information you choose to share. We also automatically
          collect certain information when you use the Services, including device identifiers, IP
          address, browser type, access times, crash data, and usage information about how you
          interact with features, pages, and content. If you enable location services, we may collect
          precise geolocation data to personalize recommendations and surface nearby experiences. We
          may receive information from third-party partners such as analytics providers, payment
          processors, social platforms you connect, or business partners that help us deliver the
          Services.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We use the collected information to operate, maintain, and improve the Services; personalize
          your experience; process transactions and bookings; communicate with you about updates,
          promotions, and support; monitor and analyze trends and usage; detect, prevent, and address
          technical issues or fraudulent activity; comply with legal obligations; and enforce our Terms
          of Service or other agreements.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          3. Sharing of Information
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We may share your information with trusted service providers who perform services on our
          behalf, such as hosting, analytics, customer support, payment processing, and marketing. We
          may share information to facilitate bookings and communications between travelers, hosts,
          and experience providers. We may disclose information in response to legal requests, to
          protect our rights, users, or the public, or in connection with a business transaction such as
          a merger or acquisition. We do not sell your personal information.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          4. Data Retention
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We retain your information for as long as necessary to provide the Services, comply with our
          legal obligations, resolve disputes, and enforce agreements. When no longer needed, we will
          delete or anonymize your data in accordance with applicable law.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          5. Your Choices and Rights
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          You can access and update certain account information by logging into ifinds. You may opt
          out of marketing communications by following the unsubscribe instructions or adjusting your
          notification preferences. Depending on your location, you may have rights to request access,
          correction, deletion, restriction, or portability of your personal data. To exercise these
          rights, contact us using the details below. We may need to verify your identity before
          fulfilling your request.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          6. Security
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We implement reasonable technical and organizational measures to protect your information
          against unauthorized access, alteration, disclosure, or destruction. However, no method of
          transmission or storage is completely secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          7. Children&apos;s Privacy
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          ifinds is not intended for children under the age of 13, and we do not knowingly collect
          personal information from children. If we learn that we have collected personal data from a
          child without verification of parental consent, we will take steps to delete that information.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          8. International Data Transfers
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          Your information may be transferred to and processed in countries other than your own. These
          countries may have data protection laws that are different from your jurisdiction. We take
          appropriate safeguards to ensure that your personal data remains protected in accordance with
          this Privacy Policy.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          9. Changes to This Policy
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          We may update this Privacy Policy from time to time. When we do, we will revise the &quot;Last
          updated&quot; date and post the new policy on this page. Your continued use of the Services after
          the updated policy becomes effective signifies your acceptance.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          10. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
          If you have any questions or concerns about this Privacy Policy or our data practices, please
          contact us at:
        </Typography>

        <Box sx={{ bgcolor: '#F5F5F5', p: 3, borderRadius: 2, mb: 6 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
           iFinds
            <br />
            Email: bilvacorp@gmail.com
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ display: 'block', color: '#9E9E9E' }}>
          Your trust matters. Thank you for choosing ifinds.
        </Typography>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;


